import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, UserRound } from "lucide-react";
import { profilesList, profilesAdd, profilesDelete, errorMessage } from "./api";
import { card, field } from "./ui";
import LinkChild from "./LinkChild";

// Learner profiles: 1 on Premium, up to 5 on Family. On Family this doubles as the family dashboard.
export default function ProfilesPage({ token, goPlans, onChanged, goOverview }) {
  const [d, setD] = useState(null);
  const [name, setName] = useState("");
  const [age, setAge] = useState("");
  const load = useCallback(() => profilesList(token).then(setD).catch((e) => toast.error(errorMessage(e))), [token]);
  useEffect(() => { load(); }, [load]);

  const add = async (e) => {
    e.preventDefault();
    try { await profilesAdd(token, { display_name: name, age_band: age || null }); setName(""); setAge(""); load(); }
    catch (err) { toast.error(errorMessage(err)); }
  };
  const remove = async (p) => {
    if (!window.confirm(`Remove ${p.display_name}'s profile?`)) return;
    try { await profilesDelete(token, p.profile_id); load(); } catch (err) { toast.error(errorMessage(err)); }
  };
  if (!d) return <div className="text-forest-700">Loading…</div>;

  return (
    <div className="space-y-5" data-testid="profiles">
      <h1 className="font-display font-semibold text-2xl text-forest-700">{d.family_dashboard ? "Family dashboard" : "Learner profiles"}</h1>
      <LinkChild token={token} onChanged={onChanged} goOverview={goOverview} />
      {d.limit === 0 ? (
        <div className={`${card} text-sm`} data-testid="profiles-locked">
          Learner profiles come with Premium (one profile) and Family (up to 5).{" "}
          <button type="button" className="text-forest-700 font-semibold hover:underline" onClick={goPlans}>See plans</button>
        </div>
      ) : (<>
        <p className="text-sm text-foreground/70">{d.learners.length} of {d.limit} profile{d.limit !== 1 ? "s" : ""} used.</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {d.learners.map((p) => (
            <div key={p.profile_id} className={`${card} flex items-center gap-3`} data-testid="profile-card">
              <span className="h-10 w-10 rounded-full bg-forest-600/10 text-forest-700 grid place-items-center"><UserRound className="h-5 w-5" /></span>
              <div className="flex-1"><div className="font-semibold text-forest-700">{p.display_name}</div><div className="text-xs text-foreground/60">{p.age_band || "Learner"}</div></div>
              <button type="button" aria-label={`Remove ${p.display_name}`} className="p-2 text-red-600" onClick={() => remove(p)}><Trash2 className="h-4 w-4" /></button>
            </div>))}
        </div>
        {d.learners.length < d.limit ? (
          <form onSubmit={add} className={`${card} flex flex-wrap gap-2 items-center`}>
            <input required className={`${field} sm:w-60`} placeholder="Learner's name" value={name} onChange={(e) => setName(e.target.value)} data-testid="profile-name" />
            <input className={`${field} sm:w-36`} placeholder="Age (optional)" value={age} onChange={(e) => setAge(e.target.value)} />
            <button type="submit" className="btn-primary" data-testid="profile-add"><Plus className="h-4 w-4" /> Add profile</button>
          </form>
        ) : <div className="text-sm text-foreground/70">You&apos;ve used every profile on your plan.</div>}
      </>)}
    </div>
  );
}
